/**
 * Font Awesome integration for `<aktion-app>`.
 *
 * Icons across the library are referenced by Font Awesome name (without the
 * `fa-` prefix). The optional `variant:` prefix (`solid:`, `regular:`,
 * `brands:`) picks the FA style — when omitted, it defaults to `solid`.
 *
 * The custom element auto-loads the CDN stylesheet on connect (once per
 * page and once per shadow root) so host apps do not have to add anything
 * to make icons render.
 */

import { isRenderableSvgMarkup } from "../library/svg-sanitizer.js";

export const FONT_AWESOME_CDN_URL =
  "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css";

export const FONT_AWESOME_VERSION = "6.7.2";

/**
 * Subresource-integrity hash for {@link FONT_AWESOME_CDN_URL}.
 *
 * Empty by default: publishing a wrong hash would silently break icon
 * rendering for every consumer, and the digest must be verified against the
 * exact file the pinned version serves before it can be asserted here. Set it
 * (`sha384-…`) to have the browser reject a tampered stylesheet, or self-host
 * the CSS and never call `ensureFontAwesomeLoaded`.
 */
export const FONT_AWESOME_CDN_INTEGRITY = "";

const LINK_MARKER_ATTR = "data-rui-font-awesome";

/**
 * The Font Awesome styles a `variant:` prefix may name. An unknown prefix falls
 * back to {@link DEFAULT_VARIANT}. The DSL types print the `${variant}:${name}`
 * icon-name template from this set.
 */
export const SUPPORTED_VARIANTS: ReadonlySet<string> = new Set(["solid", "regular", "brands"]);

const DEFAULT_VARIANT = "solid";

/**
 * Inject the Font Awesome stylesheet into `document.head` (once per page)
 * and into the given shadow root (once per instance) so icon classes
 * resolve both outside and inside the custom element.
 *
 * Safe to call from every `connectedCallback`.
 */
export function ensureFontAwesomeLoaded(shadow: ShadowRoot): void {
  if (typeof document !== "undefined") {
    injectLink(document.head, document);
  }
  injectLink(shadow, shadow.ownerDocument ?? document);
}

function injectLink(root: ParentNode, doc: Document): void {
  // happy-dom logs disallowed external stylesheet loads to stderr before
  // throwing, so a plain try/catch isn't enough to keep the test output
  // clean. Detect the test environment and skip — there is no browser to
  // render icons into anyway.
  if (isHappyDomEnvironment()) return;

  const existing = root.querySelector(
    `link[${LINK_MARKER_ATTR}="${FONT_AWESOME_VERSION}"]`,
  );
  if (existing) return;
  const link = doc.createElement("link");
  link.rel = "stylesheet";
  link.href = FONT_AWESOME_CDN_URL;
  // A third-party stylesheet on the host page is a supply-chain dependency:
  // whoever controls the CDN controls CSS in this document. `crossorigin`
  // is required for SRI to be enforced, and `referrerpolicy` avoids leaking
  // the host URL to the CDN. Hosts that cannot accept the CDN at all should
  // self-host the stylesheet and skip `ensureFontAwesomeLoaded`.
  link.crossOrigin = "anonymous";
  link.referrerPolicy = "no-referrer";
  if (FONT_AWESOME_CDN_INTEGRITY) link.integrity = FONT_AWESOME_CDN_INTEGRITY;
  link.setAttribute(LINK_MARKER_ATTR, FONT_AWESOME_VERSION);
  try {
    root.appendChild(link);
  } catch {
    // Defensive: some DOM implementations may still throw when appending an
    // external stylesheet. Production browsers never throw here.
  }
}

function isHappyDomEnvironment(): boolean {
  const g = globalThis as { happyDOM?: unknown };
  return typeof g.happyDOM !== "undefined";
}

/**
 * Resolve a Aktion icon string into Font Awesome class tokens.
 *
 * - `"house"` → `["fa-solid", "fa-house"]`
 * - `"regular:star"` → `["fa-regular", "fa-star"]`
 * - `"brands:github"` → `["fa-brands", "fa-github"]`
 *
 * Invisible Unicode glyph modifiers (variation selectors U+FE0E/U+FE0F and
 * the zero-width joiner U+200D) are stripped before validation — they often
 * sneak in when an LLM copies an FA name out of a doc that previously used
 * an emoji glyph, and would otherwise break ASCII-only validation.
 *
 * Returns an empty array for blank input or names that contain non-ASCII
 * characters (legacy emoji input — the caller renders those inline as text).
 */
export function resolveIconClasses(value: unknown): string[] {
  if (typeof value !== "string") return [];
  const sanitized = stripInvisibleModifiers(value).trim();
  if (!sanitized) return [];
  if (!isAsciiIconName(sanitized)) return [];

  const [variant, name] = splitVariant(sanitized);
  if (!name) return [];
  return [`fa-${variant}`, `fa-${name}`];
}

const INVISIBLE_MODIFIER_RE = /[\uFE0E\uFE0F\u200D\u200C\uFEFF]/g;

function stripInvisibleModifiers(input: string): string {
  return input.replace(INVISIBLE_MODIFIER_RE, "");
}

/**
 * True when the value should be treated as a Font Awesome name (vs legacy
 * emoji text that the renderer should print inline).
 */
export function isIconName(value: unknown): value is string {
  return resolveIconClasses(value).length > 0;
}

function splitVariant(input: string): [string, string] {
  const idx = input.indexOf(":");
  if (idx === -1) return [DEFAULT_VARIANT, input];
  const variant = input.slice(0, idx).trim().toLowerCase();
  const name = input.slice(idx + 1).trim();
  if (!SUPPORTED_VARIANTS.has(variant)) return [DEFAULT_VARIANT, name || input];
  return [variant, name];
}

function isAsciiIconName(value: string): boolean {
  // FA names are kebab-case ASCII (letters, digits, dashes). Allow the
  // optional `variant:` prefix. Anything else (emoji, accented text)
  // falls back to inline text rendering.
  return /^[a-zA-Z0-9:_-]+$/.test(value);
}

export const ICON_SIZES = ["xs", "sm", "md", "lg", "xl"] as const;
export type IconSize = (typeof ICON_SIZES)[number];

export function isIconSize(value: unknown): value is IconSize {
  return typeof value === "string" && (ICON_SIZES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------------ *
 * Custom icon sets (suggestions-global IX.2)
 *
 * Authors can register their own named icons (inline SVG markup) so brand
 * glyphs aren't limited to Font Awesome. Registered via
 * `$theme({ icons: { logo: "<path …/>" } })` or the host method
 * `el.registerIcons({...})`. A registered name wins over the FA lookup.
 * Values are sanitised (script / event-handler / external-entity payloads
 * stripped) before they ever reach the DOM.
 * ------------------------------------------------------------------------ */

const customIcons = new Map<string, string>();
const CUSTOM_ICON_NAME_RE = /^[a-zA-Z0-9:_-]+$/;

/**
 * Gate icon markup at registration time. The real defence is the allow-list
 * sanitiser applied at render (`renderIcon` → `sanitiseSvgMarkup`); this only
 * rejects values that could never render anything, so a typo'd or hostile
 * entry never makes it into the registry. Registration is reachable from DSL
 * via `$theme({ icons: … })`, so it must not trust its input.
 */
function sanitiseIconMarkup(raw: unknown): string {
  const s = typeof raw === "string" ? raw : "";
  if (!s || s.length > 16 * 1024) return "";
  if (!isRenderableSvgMarkup(s)) return "";
  return s;
}

/**
 * Register one or more custom icons. Each value is inline SVG markup — either
 * a full `<svg>…</svg>` element or just its inner shapes (wrapped in a
 * 24×24 `viewBox` svg automatically). Returns the names successfully added.
 */
export function registerIcons(record: unknown): string[] {
  if (!record || typeof record !== "object" || Array.isArray(record)) return [];
  const added: string[] = [];
  for (const [name, markup] of Object.entries(record as Record<string, unknown>)) {
    if (!CUSTOM_ICON_NAME_RE.test(name)) continue;
    const safe = sanitiseIconMarkup(markup);
    if (!safe) continue;
    customIcons.set(name, safe);
    added.push(name);
  }
  return added;
}

/** Look up a registered custom icon's sanitised SVG markup, or null. */
export function getCustomIcon(name: unknown): string | null {
  if (typeof name !== "string") return null;
  return customIcons.get(name.trim()) ?? null;
}

/** True when `name` resolves to a registered custom icon. */
export function hasCustomIcon(name: unknown): boolean {
  return typeof name === "string" && customIcons.has(name.trim());
}

